'use client';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Clock, Target, MessageSquare, Eye, BellRing, Briefcase, LucideIcon } from 'lucide-react';
import Link from 'next/link';
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Define explicit types
type CandidateColor = "true-blue" | "thistle";

interface Feature {
  icon: LucideIcon;
  text: string;
}

interface ProcessStep {
  step: number;
  title: string;
  description: string;
}

interface ContentType {
  title: string;
  description: string;
  features: Feature[];
  process: ProcessStep[];
  color: CandidateColor; // Use the explicit type
  ctaLink: string;
  ctaText: string;
}

// Color classes definition
const colorClasses: Record<CandidateColor, {
  bg: string;
  text: string;
  border: string;
  hoverBg: string;
  bgLight: string;
  borderLight: string;
  bgIcon: string;
  textWhite: string;
}> = {
  "true-blue": {
    bg: "bg-true-blue",
    text: "text-true-blue",
    border: "border-true-blue",
    hoverBg: "hover:bg-true-blue/90",
    bgLight: "bg-true-blue/5",
    borderLight: "border-true-blue/20",
    bgIcon: "bg-true-blue/10",
    textWhite: "text-white",
  },
  "thistle": {
    bg: "bg-thistle",
    text: "text-thistle",
    border: "border-thistle",
    hoverBg: "hover:bg-thistle/90",
    bgLight: "bg-thistle/5",
    borderLight: "border-thistle/20",
    bgIcon: "bg-thistle/10",
    textWhite: "text-white", // Assuming white text on thistle background for buttons
  }
};

export function CandidateSearchPaths() {
  const [activeTabValue, setActiveTabValue] = useState<CandidateColor>('true-blue');

  // Ensure content conforms to ContentType
  const activeContent: ContentType = {
    title: "Aktive Jobsuche",
    description: "Für alle, die aktiv nach einer neuen beruflichen Herausforderung suchen und den Prozess beschleunigen möchten. Wir unterstützen Sie mit KI-Technologie und persönlicher Beratung, um Zeit zu sparen und passende Stellen zu finden.",
    features: [
      { icon: Clock, text: "Zeitsparende Tools für Bewerbungsunterlagen und Kommunikation" },
      { icon: Target, text: "KI-gestützte Jobvorschläge basierend auf Ihrem Profil" },
      { icon: MessageSquare, text: "Kontinuierliches Feedback und Prozessbegleitung" },
    ],
    process: [
      { step: 1, title: "Profil erstellen", description: "Detailliertes Profil mit Qualifikationen, Erfahrungen und Karrierezielen" },
      { step: 2, title: "Persönliches Gespräch", description: "Individuelle Beratung zu Karrierezielen und Strategie" },
      { step: 3, title: "Maßgeschneiderte Jobvorschläge", description: "Passende Stellenangebote mit detaillierten Informationen" },
      { step: 4, title: "Bewerbungsunterstützung", description: "Optimierung der Unterlagen und Vorbereitung auf Gespräche" },
      { step: 5, title: "Kontinuierliche Begleitung", description: "Unterstützung während des gesamten Bewerbungsprozesses" },
    ],
    color: "true-blue",
    ctaLink: "/candidates/aktive-jobsuche",
    ctaText: "Mehr zur aktiven Suche",
  };

  const passiveContent: ContentType = {
    title: "Passive Jobsuche",
    description: "Für Fach- und Führungskräfte, die mit ihrer aktuellen Position zufrieden sind, aber offen für passende neue Chancen. Wir sorgen diskret dafür, dass die richtigen Unternehmen auf Sie aufmerksam werden.",
    features: [
      { icon: Eye, text: "Absolut vertrauliche Vermittlung - maximale Diskretion garantiert" },
      { icon: BellRing, text: "Selektive Jobangebote, die Ihren Vorstellungen entsprechen" },
      { icon: Briefcase, text: "Exklusive Karriereangebote, die nicht öffentlich ausgeschrieben sind" },
    ],
    process: [
      { step: 1, title: "Diskretes Profil anlegen", description: "Vertrauliches Profil ohne öffentliche Sichtbarkeit" },
      { step: 2, title: "Karriereerwartungen definieren", description: "Präzise Definition Ihrer Wechselkriterien" },
      { step: 3, title: "Passives Matching", description: "KI-gestützter Abgleich mit Stellenangeboten und Unternehmensanfragen" },
      { step: 4, title: "Exklusive Angebote erhalten", description: "Diskrete Information über passende Karrieremöglichkeiten" },
      { step: 5, title: "Begleitete Kontaktaufnahme", description: "Vermittlung und Begleitung bei Interesse" },
    ],
    color: "thistle",
    ctaLink: "/candidates/passive-jobsuche",
    ctaText: "Mehr zur passiven Suche",
  };

  const content = activeTabValue === 'true-blue' ? activeContent : passiveContent;
  const activeColor = colorClasses[content.color];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-h2 mb-6">Zwei Wege zum Traumjob</h2>
          <p className="text-body-lg max-w-4xl mx-auto text-foreground/80">
            Je nach Ihrer aktuellen Situation bieten wir Ihnen unterschiedliche Ansätze für Ihre Karriereentwicklung.
          </p>
        </div>

        {/* Toggle Switch */}
        <div className="flex justify-center mb-12">
          <Tabs 
            defaultValue={activeTabValue} 
            onValueChange={(value) => setActiveTabValue(value as CandidateColor)}
            className="w-auto"
          >
            <TabsList className={cn(
              "inline-flex rounded-full bg-floral-white-300 p-1 space-x-1 shadow-inner", 
              "h-auto"
            )}>
              <TabsTrigger
                value="true-blue"
                className={cn(
                  // Base styles 
                  "px-6 py-3 md:px-8 md:py-4 rounded-full transition-colors duration-300 font-semibold", 
                  // Inactive state: Blue text, light blue hover bg
                  "data-[state=inactive]:text-true-blue data-[state=inactive]:hover:bg-true-blue/10",
                  // Active state for true-blue
                  "data-[state=active]:bg-true-blue data-[state=active]:text-white data-[state=active]:shadow-md"
                )}
              >
                {activeContent.title} 
              </TabsTrigger>
              <TabsTrigger
                value="thistle"
                className={cn(
                   // Base styles 
                  "px-6 py-3 md:px-8 md:py-4 rounded-full transition-colors duration-300 font-semibold",
                  // Inactive state: Thistle text, light thistle hover bg
                  "data-[state=inactive]:text-thistle data-[state=inactive]:hover:bg-thistle/10",
                  // Active state for thistle
                  "data-[state=active]:bg-thistle data-[state=active]:text-white data-[state=active]:shadow-md"
                )}
              >
                {passiveContent.title}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Content Area */}
        <div className={cn(
            "max-w-6xl mx-auto mb-10 p-8 rounded-2xl border",
            activeColor.bgLight, 
            activeColor.borderLight
          )}
        > 
          <h3 className={`text-h3 ${activeColor.text} mb-8 text-center`}>{content.title}</h3>
          <div className="grid md:grid-cols-2 gap-10 items-start">
            {/* Left Column: Description & Features */}
            <div>
              <p className="text-foreground mb-8">
                {content.description}
              </p>
              <div className="space-y-4">
                {content.features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className={cn("p-2 rounded-full mr-3 mt-0.5 flex-shrink-0", activeColor.bgIcon)}>
                      <feature.icon className={cn("w-5 h-5", activeColor.text)} />
                    </div>
                    <p className="text-foreground">{feature.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Process Steps */}
            <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm">
              <h4 className={cn("text-h5 mb-5 font-semibold", activeColor.text)}>Unser Prozess</h4>
              <div className="space-y-5">
                {content.process.map((item) => (
                  <div key={item.step} className="flex">
                    <div className={cn(
                      "w-8 h-8 text-sm rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0", 
                      activeColor.bg, 
                      activeColor.textWhite,
                    )}>
                      {item.step}
                    </div>
                    <div>
                      <h5 className="font-semibold mb-0.5 text-base">{item.title}</h5>
                      <p className="text-foreground/80 text-sm">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
           <div className="text-center mt-10">
             <Link 
                href={content.ctaLink}
                className={cn("inline-block font-semibold hover:underline", activeColor.text)}
              >
                {content.ctaText} →
              </Link>
           </div>
        </div>
      </div>
    </section>
  );
} 